import React, { useState, useEffect, useCallback, useRef } from "react";
import {
	CircularProgress,
	Grid,
	Box,
	Typography,
	TextField,
	Button,
} from "@material-ui/core";
import Popover from "@material-ui/core/Popover";

import RestorePageOutlinedIcon from "@material-ui/icons/RestorePageOutlined";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import CancelIcon from "@material-ui/icons/Cancel";

import { withStyles } from "@material-ui/core/styles";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Input from "@material-ui/core/Input";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import ListItemText from "@material-ui/core/ListItemText";
import Select from "@material-ui/core/Select";
import Checkbox from "@material-ui/core/Checkbox";
import Chip from "@material-ui/core/Chip";
import ListSubheader from "@material-ui/core/ListSubheader";
import _without from "lodash/without";
import { useDropzone } from "react-dropzone";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import Alert from "@material-ui/lab/Alert";
import FormHelperText from '@mui/material/FormHelperText';

import { ApplicationDataAPI } from "../../API/ApplicationDataAPI";
import { DocsDataAPI } from "../../API/DocsDataAPI";

const useStyles = makeStyles((theme) => ({
	formControl: {
		width: "100%",
	},
	chips: {
		display: "flex",
		flexWrap: "wrap",
	},
	chip: {
		margin: 2,
	},
	noLabel: {
		marginTop: theme.spacing(3),
	},
	primaryButton: {
		backgroundColor: "#4053b3",
		color: "white",
		borderRadius: "3px",
		height: "35px",
		padding: "10px",
		"&:hover": {
			backgroundColor: "#4053b370",
		},
	},
	secondaryButton: {
		backgroundColor: "white",
		color: "#4053b3",
		borderRadius: "3px",
		height: "35px",
		padding: "10px",
		border: "1px solid #4053b3",
		"&:hover": {
			backgroundColor: "#4053b330",
		},
	},
	headerText: {
		fontSize: "1.1rem !important",
		padding: "0.25rem 0.75rem",
		paddingLeft: "0px",
		color: "#2d2d2d",
		fontWeight: "bold",
	},
}));

const CustomCheckbox = withStyles({
	root: {
		color: "#4053b3",
		"&$checked": {
			color: "#4053b3",
		},
	},
	checked: {},
})((props) => <Checkbox color="default" {...props} />);

export default function NewResubmitRequest({
	onClose,
	anchorEl,
	setResubmitEl,
	app,
}) {
	const open = Boolean(anchorEl);
	const id = open ? "docs-popover" : undefined;

	const classes = useStyles();
	const boxRef = useRef(null);

	const [reasons, setReasons] = React.useState([]);
	const [notes, setNotes] = React.useState("");
	const [documents, setDocuments] = useState([]);

	/* Document Uploading */
	const [timeoutApplicationID, setApplicationTimeoutID] = useState(0);
	const [fileNameError, setFileNameError] = useState(false);
	const [fileSizeError, setFileSizeError] = useState(false);
	const [fileTypeError, setFileTypeError] = useState(false);
	const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);

	const [newOfferItems, setNewOfferItems] = useState([]);
	const [caseByCaseItems, setCaseByCaseItems] = useState([]);
	const [otherItems, setOtherItems] = useState([]);

	useEffect(() => {
		retrievePicklist();
	}, []);

	const retrievePicklist = async () => {
		let picklistResponse = await DocsDataAPI.getResubmitPicklistItems(
			app.locked_by_email
		);

		for (const item of picklistResponse) {
			if (item.includes("1_")) {
				setNewOfferItems((prevOfferItems) => [...prevOfferItems, item]);
			} else if (item.includes("2_")) {
				setCaseByCaseItems((prevCaseByCaseItems) => [...prevCaseByCaseItems, item]);
			} else if (item.includes("3_")) {
				setOtherItems((prevOtherItems) => [...prevOtherItems, item]);
			} else {
				setOtherItems((prevOtherItems) => [...prevOtherItems, item]);
			}
		}
	};

	const handleChange = (event) => {
		setReasons(event.target.value);
	};

	const handleDelete = (e, value) => {
		e.preventDefault();
		setReasons((current) => _without(current, value));
	};

	function getExtension(filename) {
		return filename.split(".").pop();
	}

	const bytesToSizeCompare = (bytes, limit) => {
		const MiB = 1048576; // 1 MiB = 1048576 bytes
		const limitMiB = limit || 100; // 100MB by default

		if (bytes < limitMiB * MiB) {
			return false; // File is less than 100MB
		} else {
			return true; // File is 100MB or larger
		}
	};

	const clearFileErrorTimeout = () => {
		if (timeoutApplicationID) {
			setFileNameError(false);
			setFileSizeError(false);
			setFileTypeError(false);
			clearTimeout(timeoutApplicationID);
		}
	};

	useEffect(() => {
		let appid;

		if (fileNameError) {
			appid = window.setTimeout(() => {
				setFileNameError(false);
			}, 5000);
			setApplicationTimeoutID(appid);
		}

		return () => {
			if (appid) {
				clearTimeout(appid);
			}
		};
	}, [fileNameError]);

	useEffect(() => {
		let appid;

		if (fileSizeError) {
			appid = window.setTimeout(() => {
				setFileSizeError(false);
			}, 5000);
			setApplicationTimeoutID(appid);
		}

		return () => {
			if (appid) {
				clearTimeout(appid);
			}
		};
	}, [fileSizeError]);

	useEffect(() => {
		let appid;

		if (fileTypeError) {
			appid = window.setTimeout(() => {
				setFileTypeError(false);
			}, 5000);
			setApplicationTimeoutID(appid);
		}

		return () => {
			if (appid) {
				clearTimeout(appid);
			}
		};
	}, [fileTypeError]);

	const createResubmit = async () => {
		setIsSubmittingRequest(true);
		let reasonsFormatted = "";

		try {
			for (const reason of reasons) {
				reasonsFormatted = reasonsFormatted + `${reason};`;
			}

			let ResubmitResponse = await ApplicationDataAPI.createResubmit(
				app.id,
				app.locked_by_email,
				reasonsFormatted,
				notes
			);

			//upload each file here with the case number
			for (const document of documents) {
				let uploadedDoc = await uploadFile(
					document.data,
					"Resubmit",
					ResubmitResponse.case_number
				);
				console.log("uploadedDoc: ", uploadedDoc);
			}

			window.location.reload();
		} catch (error) {
			console.log("error: ", error);
			setIsSubmittingRequest(false);
		}
	};

	const uploadFile = async (selectedFile, selectedType, caseID) => {
		try {
			const documentData =
				await DocsDataAPI.uploadMultipleDocumentsToFileStoreResubmit(
					app.id,
					app.broker_id,
					selectedFile,
					selectedType,
					null,
					app.locked_by_name,
					null,
					null,
					null,
					app.locked_by_email,
					caseID
				);

			return documentData;
		} catch (e) {
			console.error("Uploading error", e);
			alert(e);
			throw e;
		}
	};

	function checkFormValidation() {
		if (reasons.length >= 1 && notes && documents.length >= 1) {
			return false;
		}

		return true;
	}

	const resubmitHeader = () => {
		return (
			<Box style={{ backgroundColor: "#4053b3", height: "80px" }} id="testID">
				<Grid
					container
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						height: "100%",
					}}>
					<Grid
						item
						xs={2}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<RestorePageOutlinedIcon style={{ color: "white", fontSize: "35px" }} />
					</Grid>
					<Grid item xs={8}>
						<Typography
							variant="subtitle1"
							style={{ textAlign: "left", fontSize: "20px", color: "white" }}>
							New Resubmit Request
						</Typography>
					</Grid>
					<Grid
						item
						xs={2}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<IconButton
							disabled={isSubmittingRequest}
							onClick={() => {
								setResubmitEl(null);
							}}
							style={{ color: "white" }}>
							<CloseIcon style={{ color: "white" }} />
						</IconButton>
					</Grid>
				</Grid>
			</Box>
		);
	};

	const ReasonsUI = () => {
		return (
			<>
				<Box style={{ display: "flex", marginTop: "20px", marginBottom: "10px" }}>
					<Typography
						className={classes.headerText}
						style={{
							color: "red",
						}}>
						*
					</Typography>
					<Typography className={classes.headerText}>Reason(s)</Typography>
				</Box>

				<FormControl
					variant="outlined"
					className={classes.formControl}
					disabled={isSubmittingRequest}>
					<Select
						labelId="demo-simple-select-outlined-label"
						id="demo-simple-select-outlined"
						multiple
						value={reasons}
						disabled={isSubmittingRequest}
						onChange={handleChange}
						input={<Input id="select-multiple-chip" />}				
						renderValue={(selected) => (
							<div className={classes.chips} disabled={isSubmittingRequest}>
								{selected.map((value) =>
									value != undefined ? (
										<Chip
											disabled={isSubmittingRequest}
											label={value.split("_")[1]}
											className={classes.chip}
											clickable
											deleteIcon={
												<CancelIcon
													disabled={isSubmittingRequest}
													onMouseDown={(event) => event.stopPropagation()}
												/>
											}
											onDelete={(e) => handleDelete(e, value)}
										/>
									) : null
								)}
							</div>
						)}
						MenuProps={{
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "left",
							},
							transformOrigin: {
								vertical: "top",
								horizontal: "left",
							},
							getContentAnchorEl: null,
							PaperProps: {
								style: {
									maxHeight: "350px",
									width: "250px",
									marginTop: "8px",
								},
							},
						}}>
						<ListSubheader style={{ backgroundColor: "white" }}>
							New Offer / Decline Letter
						</ListSubheader>

						{newOfferItems.map((name) => (
							<MenuItem key={name} value={name}>
								<CustomCheckbox checked={reasons.indexOf(name) > -1} />
								<Typography variant="body2">{name.split("_")[1]}</Typography>
							</MenuItem>
						))}

						<ListSubheader style={{ backgroundColor: "white" }}>
							Case by Case
						</ListSubheader>
						{caseByCaseItems.map((name) => (
							<MenuItem key={name} value={name}>
								<CustomCheckbox checked={reasons.indexOf(name) > -1} />
								<Typography variant="body2">{name.split("_")[1]}</Typography>
							</MenuItem>
						))}

						<ListSubheader style={{ backgroundColor: "white" }}>Other</ListSubheader>
						{otherItems.map((name) => (
							<MenuItem key={name} value={name}>
								<CustomCheckbox checked={reasons.indexOf(name) > -1} />
								<Typography variant="body2">{name.split("_")[1]}</Typography>
							</MenuItem>
						))}
					</Select>
					<FormHelperText style={{color: "red", display: reasons.length > 0 ? "none" : "block"}}>Select reasons for the resubmit</FormHelperText>
				</FormControl>
			</>
		);
	};

	const NotesUI = () => {
		return (
			<>
				<Box style={{ display: "flex", marginTop: "20px", marginBottom: "10px" }}>
					<Typography
						className={classes.headerText}
						style={{
							color: "red",
						}}>
						*
					</Typography>
					<Typography className={classes.headerText}>Notes</Typography>
				</Box>

				<TextField
					id="outlined-multiline-static"
					multiline
					minRows={4}
					maxRows={4}
					value={notes}
					placeholder="Enter your notes..."
					disabled={isSubmittingRequest}
					onChange={(e) => setNotes(e.target.value)}
					variant="outlined"
					style={{ width: "100%" }}
				/>
			</>
		);
	};

	const onDocumentsDropZone = async (acceptedFiles) => {
		clearFileErrorTimeout();

		for (const file of acceptedFiles) {
			if (getExtension(file.name).toLowerCase() !== "pdf") {
				setFileTypeError(true);
				return;
			} else if (file.name.length > 80) {
				setFileNameError(true);
				return;
			} else if (bytesToSizeCompare(file.size, 100)) {
				setFileSizeError(true);
				return;
			} else {
				try {
					const newDocument = {
						data: file,
						document_name: file.name,
					};

					setDocuments((prevDocuments) => [...prevDocuments, newDocument]);
				} catch (error) {
					// Handle the error as needed
					console.error("Error uploading file", error);
				}
			}
		}
	};

	const documentDropUI = () => {
		return (
			<>
				<Box
					style={{
						display: isSubmittingRequest ? "none" : "flex",
						marginTop: "20px",
						marginBottom: "10px",
					}}>
					<Typography
						className={classes.headerText}
						style={{
							color: "red",
						}}>
						*
					</Typography>
					<Typography className={classes.headerText}>Attachments</Typography>
				</Box>

				<div
					style={{ display: isSubmittingRequest ? "none" : "flex", width: "100%" }}
					onClick={() => clearFileErrorTimeout()}>
					<div
						{...documentsDropZone.getRootProps()}
						style={{
							backgroundColor: "#F7F6F6",
							border: "2px dashed #cbd5e1",
							pointerEvents: "auto",
							marginBottom: "20px",
							width: "100%",
						}}>
						<input {...documentsDropZone.getInputProps()} />
						<Box style={{ padding: "0px 0px", textAlign: "center" }}>
							<Grid container direction="column" style={{ display: "flex" }}>
								<Grid
									item
									xs={12}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										height: "50px",
									}}>
									<CloudDownloadIcon
										style={{
											width: "35px",
											height: "35px",
											color: "#3498db",
											cursor: "pointer",
											marginTop: "5px",
											marginBottom: "5px",
										}}
									/>
								</Grid>

								<Grid
									item
									xs={12}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										height: "50px",
									}}>
									<Typography
										variant="body2"
										style={{
											textAlign: "center",
											fontSize: "15px",
											color: "black",
											marginTop: "5px",
											marginBottom: "5px",
										}}>
										Drop file here or{" "}
										<span
											style={{
												color: "#3498db",
												cursor: "pointer",
												textDecoration: "underline",
												textUnderlineOffset: "3px",
											}}>
											click to select file
										</span>
									</Typography>
								</Grid>
							</Grid>
						</Box>
					</div>
				</div>
			</>
		);
	};

	const checkForDocument = () => {
		return (
			<>
				<Grid
					container
					flexDirection="row"
					style={{
						display: "flex",
						marginTop: isSubmittingRequest ? "20px" : "0px",
					}}>
					{documents.map((row, index) => (
						<>
							<Box
								style={{
									display: "flex",
									width: "100%",
									marginBottom: "10px",
								}}>
								<Grid
									item
									xs={10}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}>
									<Box
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "start",
											width: "100%",
											backgroundColor: "#00000010",
											color: "#3498db",
											padding: "10px 0px 10px 20px",
										}}>
										<Typography style={{ cursor: "default" }} variant="body2">
											{row.document_name}
										</Typography>
									</Box>
								</Grid>
								<Grid
									item
									xs={2}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: "#00000010",
										color: "#3498db",
									}}>
									<CancelOutlinedIcon
										style={{
											width: "25px",
											height: "25px",
											color: "#444444",
											cursor: "pointer",
											display: isSubmittingRequest ? "none" : "block",
										}}
										onClick={() => {
											//handleFileDelete(index, row.document_name, row.id);
											setDocuments((prevDocuments) => {
												// Filter out the document at the specified index
												return prevDocuments.filter((_, i) => i !== index);
											});
										}}
									/>
								</Grid>
							</Box>
						</>
					))}

					{fileNameError ? (
						<Box
							style={{
								marginTop: "10px",
								marginBottom: "10px",
								width: "100%",
							}}>
							<Alert severity="error" style={{ textAlign: "left" }}>
								The file name exceeds our limit of 80 characters. Please shorten and try
								again.
							</Alert>
						</Box>
					) : fileSizeError ? (
						<Box
							style={{
								marginTop: "10px",
								marginBottom: "10px",
								width: "100%",
							}}>
							<Alert severity="error">
								The File you are trying to upload is over the 100MB limit.
							</Alert>
						</Box>
					) : fileTypeError ? (
						<Box
							style={{
								marginTop: "10px",
								marginBottom: "10px",
								width: "100%",
							}}>
							<Alert severity="error">
								Invalid document type, please upload a PDF.
							</Alert>
						</Box>
					) : null}
				</Grid>
			</>
		);
	};

	const documentsDropZone = useDropzone({
		onDrop: onDocumentsDropZone,
	});

	const bottomButtonsUI = () => {
		return (
			<>
				<Grid
					container
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "end",
						height: "100%",
					}}>
					<Grid
						item
						xs={12}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "end",
							height: "100%",
							marginTop: "30px",
						}}>
						<Button
							color="secondary"
							className={classes.secondaryButton}
							disabled={isSubmittingRequest}
							onClick={() => {
								setResubmitEl(null);
							}}
							style={{ marginRight: "20px" }}>
							Cancel
						</Button>
						<Button
							color="primary"
							className={classes.primaryButton}
							disabled={isSubmittingRequest || checkFormValidation()}
							onClick={() => createResubmit()}>
							Submit Request
						</Button>
					</Grid>
				</Grid>
			</>
		);
	};

	return (
		<Popover
			id={id}
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "center",
			}}
			classes={{ paper: classes.paper }}
			transformOrigin={{
				vertical: "top",
				horizontal: "center",
			}}
			ref={boxRef}>
			<Box style={{ width: "500px" }}>
				{resubmitHeader()}

				{isSubmittingRequest ? (
					<Box
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							margin: "20px",
							padding: "0px",
						}}>
						<CircularProgress size={40} color="primary" />
					</Box>
				) : (
					<Box
						style={{
							margin: "20px",
							padding: "0px",
						}}>
						{ReasonsUI()}
						{NotesUI()}
						{documentDropUI()}
						{checkForDocument()}
						{bottomButtonsUI()}
					</Box>
				)}
			</Box>
		</Popover>
	);
}
